import type { Except, ValidatedKey } from '@festina-life/core'
import { useField } from 'formik'
import { useMemo } from 'react'
import { numericFormatter } from 'react-number-format'

import { NumberInput } from '../../forms/numberInput/NumberInput'
import type { RawNumberInputProps } from '../../forms/numberInput/RawNumberInput'
import { useNumberFormatOptions } from '../../forms/numberInput/useNumberFormatOptions'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
  handleOnBlur,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useNumericValueDifferenceCheck } from '../formikUtils/useNumericValueDifferenceCheck'
import { withFormikInputDiffing } from '../formikUtils/withFormikInputDiffing'
import type { FormikInputPropsForWrapper } from '../types'

export type FormikNumberInputProps = Except<
  RawNumberInputProps,
  'defaultValue' | 'onBlur' | 'onChange'
> & {
  name: ValidatedKey
  defaultValue?: number
  onBlur?: (value: number | undefined) => void
} & FormikInputPropsForWrapper

const FormikNumberInputInternal = ({
  name,
  isRequired,
  label,
  defaultValue,
  validationText,
  onDefaultValueClick,
  description,
  disabled,
  onBlur,
  hasRightColumnAlways,
  className,
  decimalScale,
  existingValueConfig,
  ...restProps
}: FormikNumberInputProps) => {
  const [field, meta, { setValue }] = useField(name)
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)
  const numberFormatOptions = useNumberFormatOptions(decimalScale)

  const existingValue = useExistingValuesContext()?.getExistingValue?.(name) as
    | number
    | undefined

  const formattedDefaultValue = useMemo(
    () => numericFormatter(defaultValue?.toString() ?? '', numberFormatOptions),
    [defaultValue, numberFormatOptions],
  )

  const formattedExistingValue = useMemo(
    () =>
      numericFormatter(existingValue?.toString() ?? '', numberFormatOptions),
    [existingValue, numberFormatOptions],
  )

  const inputValueDifferenceFromExistingValue =
    useNumericValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    label,
    existingValue: formattedExistingValue,
    existingValueConfig,
    inputValueDifferenceFromExistingValue,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      validationText,
      defaultValue: formattedDefaultValue,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick,
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      error,
      hasRightColumnAlways,
      className,
    },
  })

  return (
    <NumberInput
      wrapperProps={wrapperProps}
      id={name}
      isInError={isInError}
      disabled={isFieldDisabled}
      decimalScale={decimalScale}
      {...field}
      {...restProps}
      placeholder={existingValue ?? defaultValue}
      onChange={setValue}
      onBlur={(event) => handleOnBlur({ event, field, onBlur })}
    />
  )
}

export const FormikNumberInput = withFormikInputDiffing(
  FormikNumberInputInternal,
)
